import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

/**
 * Render host-authored rich text.
 *
 * The server already sanitises this on write (`utils/richText.ts`), so this is
 * the SECOND line, not the only one — but it is the line that sits closest to
 * the browser. Sanitising again here means a row written before the server
 * guard existed, or through some future path that forgets it, still cannot run
 * script in a guest's page. It costs nothing on text that is already clean.
 *
 * The allowlist deliberately mirrors the server's. If the two drift, the
 * stricter one wins and formatting silently disappears — so change both
 * together or not at all.
 */

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's',
  'ul', 'ol', 'li', 'blockquote', 'h3', 'h4', 'a',
];

interface RichTextProps {
  html?: string | null;
  className?: string;
}

const RichText: React.FC<RichTextProps> = ({ html, className = '' }) => {
  const clean = useMemo(() => {
    if (!html) return '';
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      // Anything that is not a plain navigable link is dropped rather than
      // rewritten — there is no legitimate `javascript:` in a description.
      ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel):/i,
    });
  }, [html]);

  if (!clean) return null;

  return (
    <div
      // `prose-like` spacing rather than Tailwind Typography, which the app does
      // not include: just enough that lists and paragraphs do not run together.
      className={`rich-text ${className}`}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};

export default RichText;
